import { api } from '../lib/api';

const API_BASE_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api`;

export interface CreditoTransacao {
  id: string;
  user_id: string;
  tipo: 'COMPRA' | 'RESERVA' | 'ESTORNO' | 'USADO' | 'AJUSTE';
  valor: number;
  saldo_anterior: number;
  saldo_posterior: number;
  observacao?: string;
  reserva_id?: string;
  pagamento_id?: string;
  created_at: string;
  user: {
    nome_completo: string;
    email: string;
  };
}

export interface ClienteCredito {
  user_id: string;
  nome: string;
  email: string;
  telefone: string;
  cpf: string;
  saldo: number;
  totalTransacoes: number;
  ultimaTransacao?: string;
}

export interface CreditoEstatisticas {
  totalCreditos: number;
  totalTransacoes: number;
  creditosReservados: number;
  creditosConsumidos: number;
  creditosEmUso: number;
  creditosMultas: number;
  creditosAtivos: number;
  topClientes: Array<{
    user_id: string;
    nome: string;
    saldo: number;
    totalTransacoes: number;
  }>;
  transacoesPorTipo: Record<string, number>;
}

export interface CriarTransacaoRequest {
  user_id: string;
  tipo: 'COMPRA' | 'RESERVA' | 'ESTORNO' | 'USADO' | 'AJUSTE';
  valor: number;
  observacao?: string;
  reserva_id?: string;
  pagamento_id?: string;
}

export interface AjustarCreditoRequest {
  user_id: string;
  valor: number;
  observacao: string;
  tipo: 'ADICIONAR' | 'REMOVER';
}

export interface TransferirCreditoRequest {
  de_user_id: string;
  para_user_id: string;
  valor: number;
  observacao?: string;
}

export interface TransacoesResponse {
  success: boolean;
  transacoes: CreditoTransacao[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SaldoUsuario {
  saldo: number;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null && 'response' in error;
}

function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (isApiError(error)) {
    return error.response?.data?.message || error.message || defaultMessage;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return defaultMessage;
}

export class CreditoService {
  static async getSaldoUsuario(userId: string): Promise<number> {
    try {
      console.log('🔍 Buscando saldo para usuário:', userId);
      
      const response = await api.get(`/api/creditos/saldo/${userId}`);
      
      console.log('✅ Saldo obtido:', response.data.saldo);
      return response.data.saldo;
    } catch (error: unknown) {
      console.error('Erro ao buscar saldo:', error);
      throw new Error(getErrorMessage(error, 'Erro ao buscar saldo do usuário'));
    }
  }

  static async criarTransacao(dados: CriarTransacaoRequest): Promise<CreditoTransacao> {
    try {
      console.log('💰 Criando transação:', dados);
      
      const response = await api.post('/api/creditos/transacao', dados);

      console.log('✅ Transação criada:', response.data.transacao);
      return response.data.transacao;
    } catch (error: unknown) {
      console.error('Erro ao criar transação:', error);
      throw new Error(getErrorMessage(error, 'Erro ao criar transação'));
    }
  }

  static async getTransacoesByUsuario(
    userId: string, 
    page: number = 1, 
    limit: number = 20
  ): Promise<TransacoesResponse> {
    try {
      console.log('📋 Buscando transações para usuário:', userId, { page, limit });
      
      const response = await api.get(`/api/creditos/transacoes/usuario/${userId}`, {
        params: { page, limit }
      });

      console.log('✅ Transações obtidas:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Erro ao buscar transações:', error);
      throw new Error(getErrorMessage(error, 'Erro ao buscar transações do usuário'));
    }
  }

  static async getAllTransacoes(
    page = 1, 
    limit = 20, 
    filtros?: {
      tipo?: string;
      user_id?: string;
      data_inicio?: string;
      data_fim?: string;
    }
  ): Promise<TransacoesResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filtros
      });

      const response = await api.get(`/api/creditos/transacoes?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar transações:', error);
      throw new Error('Erro ao buscar transações');
    }
  }

  static async getClientesComCredito(): Promise<ClienteCredito[]> {
    try {
      const response = await api.get('/api/creditos/clientes');
      return response.data.clientes;
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      throw new Error('Erro ao buscar clientes com crédito');
    }
  }

  static async getClientesParaAjuste(): Promise<ClienteCredito[]> {
    try {
      console.log('🔍 Buscando TODOS os clientes para ajustes...');
      const response = await api.get('/api/creditos/clientes-ajuste');
      console.log(`✅ Recebidos ${response.data.clientes.length} clientes para ajuste`);
      return response.data.clientes;
    } catch (error) {
      console.error('Erro ao buscar clientes para ajuste:', error);
      throw new Error('Erro ao buscar clientes para ajuste');
    }
  }

  static async getEstatisticas(): Promise<CreditoEstatisticas> {
    try {
      const response = await api.get('/api/creditos/estatisticas');
      return response.data.estatisticas;
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      throw new Error('Erro ao buscar estatísticas');
    }
  }

  static async ajustarCredito(dados: AjustarCreditoRequest): Promise<CreditoTransacao> {
    try {
      const response = await api.post('/api/creditos/ajustar', dados);
      return response.data.transacao;
    } catch (error: unknown) {
      console.error('Erro ao ajustar crédito:', error);
      throw new Error(getErrorMessage(error, 'Erro ao ajustar crédito'));
    }
  }

  static async transferirCredito(dados: TransferirCreditoRequest): Promise<unknown> {
    try {
      const response = await api.post('/api/creditos/transferir', dados);
      return response.data.resultado;
    } catch (error: unknown) {
      console.error('Erro ao transferir crédito:', error);
      throw new Error(getErrorMessage(error, 'Erro ao transferir crédito'));
    }
  }

  // 🆕 Marcar reserva como usada (RESERVA → USADO)
  static async marcarReservaComoUsada(
    reservaId: string, 
    userId: string, 
    valor: number, 
    observacao?: string
  ): Promise<unknown> {
    try {
      console.log('Marcando reserva como usada:', { reservaId, userId, valor });

      const response = await api.post('/api/creditos/marcar-usado', {
        reserva_id: reservaId,
        user_id: userId,
        valor: valor,
        observacao: observacao
      });

      console.log('Resposta do servidor:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Erro ao marcar reserva como usada:', error);
      throw new Error(getErrorMessage(error, 'Erro ao marcar reserva como usada'));
    }
  }

  // 🆕 Buscar transações por bloco/reserva
  static async getTransacoesByBloco(reservaId: string): Promise<CreditoTransacao[]> {
    try {
      console.log('🔍 Buscando transações para reserva:', reservaId);

      const response = await api.get(`/api/creditos/transacoes?reserva_id=${reservaId}`);

      console.log('✅ Transações da reserva obtidas:', response.data.transacoes);
      return response.data.transacoes || [];
    } catch (error: unknown) {
      console.error('Erro ao buscar transações por bloco:', error);
      throw new Error(getErrorMessage(error, 'Erro ao buscar transações'));
    }
  }

  static formatarTipoTransacao(tipo: string): string {
    const tipoMap: { [key: string]: string } = {
      'COMPRA': 'Compra',
      'RESERVA': 'Reserva',
      'ESTORNO': 'Estorno',
      'USADO': 'Usado',
      'AJUSTE': 'Ajuste'
    };

    return tipoMap[tipo] || tipo;
  }

  static obterCorTipo(tipo: string): string {
    const corMap: { [key: string]: string } = {
      'COMPRA': 'text-green-600',
      'RESERVA': 'text-orange-600',
      'ESTORNO': 'text-blue-600',
      'USADO': 'text-purple-600',
      'AJUSTE': 'text-gray-600'
    };

    return corMap[tipo] || 'text-gray-600';
  }

  static obterIconeTipo(tipo: string): string {
    const iconeMap: { [key: string]: string } = {
      'COMPRA': 'Plus',
      'RESERVA': 'Calendar',
      'ESTORNO': 'RefreshCw',
      'USADO': 'Check',
      'AJUSTE': 'Settings'
    };

    return iconeMap[tipo] || 'Coins';
  }

  static calcularEstatisticas(transacoes: CreditoTransacao[]): {
    totalCompras: number;
    totalReservas: number;
    totalEstornos: number;
    totalUsados: number;
    saldoAtual: number;
  } {
    const estatisticas = {
      totalCompras: 0,
      totalReservas: 0,
      totalEstornos: 0,
      totalUsados: 0,
      saldoAtual: 0
    };

    transacoes.forEach(transacao => {
      switch (transacao.tipo) {
        case 'COMPRA':
          estatisticas.totalCompras += transacao.valor;
          break;
        case 'RESERVA':
          estatisticas.totalReservas += Math.abs(transacao.valor);
          break;
        case 'ESTORNO':
          estatisticas.totalEstornos += transacao.valor;
          break;
        case 'USADO':
          estatisticas.totalUsados += Math.abs(transacao.valor);
          break;
      }
    });

    if (transacoes.length > 0) {
      const transacaoMaisRecente = transacoes.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
      estatisticas.saldoAtual = transacaoMaisRecente.saldo_posterior;
    }

    return estatisticas;
  }

  static formatarData(data: string): string {
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  static formatarValor(valor: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  }
} 